import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/contact.css';
import SEO from '../components/SEO';

const API_BASE_URL = process.env.REACT_APP_OPENWEB_API_BASE_URL;

const Contact: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  // Honeypot: humans never see or fill this field; bots that auto-fill inputs
  // by name will. The backend rejects any submission where it's non-empty.
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Captured once at mount so the backend can reject submissions that arrive
  // suspiciously fast (typical bot behavior).
  const formLoadedAt = useRef<number>(Date.now());
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage('');

    const url = `${API_BASE_URL}/api/contact/support?timestamp=${new Date().getTime()}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          message,
          company_website: companyWebsite,
          formLoadedAt: formLoadedAt.current,
        }),
      });
      if (response.ok) {
        setSuccessMessage(
          "Thanks — we received your message. Our team will get back to you shortly."
        );
        setName(''); setEmail(''); setMessage('');
        setTimeout(() => navigate('/'), 5000);
      } else {
        setErrorMessage('We could not submit your message. Please try again or email support@openuse.io.');
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Network error. Please check your connection or email support@openuse.io.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us"
        description="Have a question or need support? Contact the OpenUse team and we'll get back to you as soon as possible."
        path="/contact"
      />
      <div className="contact-intro">
        <p className="hero-eyebrow">Get in touch</p>
        <h1>Contact us</h1>
        <p>
          Have a question, feedback, or need a hand? Send us a note and our
          team will get back to you as soon as possible.
        </p>
      </div>

      {successMessage && <p className="success-message">{successMessage}</p>}
      {errorMessage && <p className="error-message">{errorMessage}</p>}

      <form onSubmit={handleSubmit} className="contact-form">
        {/* Honeypot — hidden from humans, irresistible to bots. */}
        <div className="hp-field" aria-hidden="true">
          <label htmlFor="company_website">Company website</label>
          <input
            id="company_website"
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
          />
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor="name">Name</label>
            <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="form-field">
            <label htmlFor="email">Email</label>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
        </div>

        <label htmlFor="message">Message</label>
        <textarea
          id="message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          placeholder="How can we help?"
          required
        />

        <button
          type="submit"
          disabled={submitting || !name.trim() || !email.trim() || !message.trim()}
        >
          {submitting ? 'Sending…' : 'Send message'}
        </button>
      </form>
    </div>
  );
};

export default Contact;
